// Ikona "suwaki" zamiast koła zębatego — bardziej intuicyjny "ustawienia/opcje"
// dropdown trigger w nagłówkach stron.
import { AdjustmentsHorizontalIcon, ArrowLeftIcon, ArrowPathIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { Chip, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@heroui/react';
import { Pencil, Settings } from 'lucide-react';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useLocaleNavigate } from '@/utils/locale';

import { BREADCRUMB_UPDATE_EVENT } from '../../utils';
import { AddButton } from '../Buttons';
import { Flex } from '../Flex';
import { BtnBack, ContextMenu, ContextMenuDivider, ContextMenuItem, MoreBoxIcon, PageTitleActionBar, PageTitleWrapper, Title } from './PageTitle.styled';

const BREADCRUMB_TITLES_KEY = 'breadcrumb_titles';

interface Options {
    name: string | ReactNode;
    action: ()=> void;
    /** Pozycja widoczna, ale nieaktywna (np. w trakcie pobierania pliku). */
    isDisabled?: boolean;
}

interface PageTitleProps {
    actions?: ReactNode;
    title?: string | ReactNode;
    actionCenter?: string | ReactNode;
    buttonOnClick?: () => void;
    showSettings?: () => void;
    isSmall?: boolean;
    isEditMode?: boolean;
    isDuplicate?: boolean;
    option?:Options[];
    stepperContainer?:ReactNode;
    skipIdRemoval?: boolean; // If true, keeps ID in path when going back
    onLanguageOpen?: () => void; // Trigger to open language drawer from gear menu
    languageSelector?: ReactNode; // LanguageContextSelector rendered outside popover
}

// Sprawdza czy segment to ID
const isIdSegment = (segment: string): boolean => {
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const firebaseIdPattern = /^[a-zA-Z0-9]{20,}$/;
  const numericPattern = /^\d+$/;

  return uuidPattern.test(segment) || firebaseIdPattern.test(segment) || numericPattern.test(segment);
};

// Sprawdza czy segment to akcja (edit, create, new, etc.)
const isActionSegment = (segment: string): boolean => {
  const actionSegments = ['edit', 'create', 'new', 'add', 'update', 'delete', 'details', 'view'];
  return actionSegments.includes(segment.toLowerCase());
};

// Pobiera ID z aktualnej ścieżki
const getIdFromPath = (pathname: string): string | null => {
  const segments = pathname.split('/').filter(Boolean);
  const idSegment = segments.find(isIdSegment);
  return idSegment || null;
};

const saveBreadcrumbTitle = (id: string, title: string): void => {
  try {
    const stored = localStorage.getItem(BREADCRUMB_TITLES_KEY);
    const titles = stored ? JSON.parse(stored) : {};
    titles[id] = { title, timestamp: Date.now() };
    localStorage.setItem(BREADCRUMB_TITLES_KEY, JSON.stringify(titles));

    window.dispatchEvent(new CustomEvent(BREADCRUMB_UPDATE_EVENT));
  } catch (e) {
  }
};

// Czyści stare wpisy (starsze niż 24h)
const cleanupOldTitles = (): void => {
  try {
    const stored = localStorage.getItem(BREADCRUMB_TITLES_KEY);
    if (stored) {
      const titles = JSON.parse(stored);
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000;

      const cleaned = Object.fromEntries(
        Object.entries(titles).filter(
          ([, value]: [string, any]) => now - value.timestamp < maxAge,
        ),
      );

      localStorage.setItem(BREADCRUMB_TITLES_KEY, JSON.stringify(cleaned));
    }
  } catch (e) {
  }
};

export const PageTitle:FC<PageTitleProps> = (props) => {
  const { t } = useTranslation();
  const {
    actions, title, buttonOnClick, isSmall = true, isEditMode, showSettings,
    actionCenter, option, stepperContainer, isDuplicate, skipIdRemoval = false,
    onLanguageOpen, languageSelector,
  } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const navigate = useLocaleNavigate();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  // Zapisz tytuł gdy jest ID w URL
  useEffect(() => {
    const currentId = getIdFromPath(location.pathname);

    if (currentId && title && typeof title === 'string') {
      saveBreadcrumbTitle(currentId, title);
    }

    cleanupOldTitles();
  }, [location.pathname, title]);

  useEffect(() => {
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const handleBack = () => {
    const currentPath = location.pathname;
    const segments = currentPath.split('/').filter(Boolean);

    if (segments.length <= 1) {
      navigate('/');
      return;
    }

    // Remove last segment (current page)
    segments.pop();

    // Only remove ID segment if skipIdRemoval is false
    if (!skipIdRemoval) {
      let lastSegment = segments[segments.length - 1];

      // If the last segment is an ID, remove it
      if (lastSegment && isIdSegment(lastSegment)) {
        segments.pop();
        lastSegment = segments[segments.length - 1];
      }

      // If the new last segment is an action segment (edit, create, etc.), also remove it
      if (lastSegment && isActionSegment(lastSegment)) {
        segments.pop();
      }
    }

    navigate(`/${segments.join('/')}`);
  };

  const handleReorderClick = () => {
    if (option && option.length > 0) {
      option[0].action();
      setIsPopoverOpen(false);
    }
  };

  return (
    <PageTitleWrapper>
      <Flex itemAlign="flex-start" justifyContent="flex-start" gap="10px" direction="row">
        {actionCenter && <div>{actionCenter}</div>}
        <Title>
          <Flex itemAlign="center" gap="10px">
            {/* Tryb edycji: ikonka długopisu zamiast etykiety. */}
            {isEditMode && (
            <Pencil size={22} strokeWidth={1.75} className="shrink-0 text-text-secondary" aria-hidden />
            )}
            {isDuplicate && (
            <Chip isDisabled color="secondary">
              Duplikat
            </Chip>
            )}
            <h1 className="text-[28px] font-medium leading-[1.3] tracking-[-0.015em] text-text-primary dark:text-white">
              {title}
            </h1>
          </Flex>
        </Title>
        <div>
          {stepperContainer}
        </div>
      </Flex>
      <div />
      <div>
        <Flex itemAlign="center" justifyContent="flex-end" gap="5px">
          {actions && actions}
          {buttonOnClick && (
            <AddButton onClick={buttonOnClick} isSmall={isSmall} data-testid="page-title-add" />
          )}
          {((option && option.length > 0) || onLanguageOpen) && (
            <PageTitleActionBar>
              <Popover
                placement="bottom-end"
                isOpen={isPopoverOpen}
                onOpenChange={setIsPopoverOpen}
                shouldBlockScroll={false}
                classNames={{
                  content: 'p-0 border border-gray-200 dark:border-[#374151] dark:bg-[#1a1f26] rounded-md shadow-[0_4px_12px_0_rgba(0,0,0,0.08)]',
                }}
              >
                <PopoverTrigger>
                  <MoreBoxIcon data-testid="page-options-menu">
                    <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </MoreBoxIcon>
                </PopoverTrigger>
                <PopoverContent>
                  <ContextMenu>
                    {option && option.map((opt, index) => (
                      <ContextMenuItem
                        key={index}
                        disabled={opt.isDisabled}
                        onClick={() => {
                          if (opt.isDisabled) return;
                          opt.action();
                          setIsPopoverOpen(false);
                        }}
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        {opt.name}
                      </ContextMenuItem>
                    ))}
                    {option && option.length > 0 && onLanguageOpen && (
                      <ContextMenuDivider />
                    )}
                    {onLanguageOpen && (
                      <ContextMenuItem
                        onClick={() => {
                          setIsPopoverOpen(false);
                          onLanguageOpen();
                        }}
                      >
                        <LanguageIcon className="w-4 h-4" />
                        {t('common.changeLanguage')}
                      </ContextMenuItem>
                    )}
                  </ContextMenu>
                </PopoverContent>
              </Popover>
            </PageTitleActionBar>
          )}
        </Flex>
      </div>
      {languageSelector}
    </PageTitleWrapper>
  );
};
